import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertBox, Breadcrumb, Button, Icon } from "@zorni/components-lib";

import BookingSteps from "../../components/booking/bookingSteps/BookingSteps";
import { usePerson } from "../../hooks/user/usePerson";
import { currentLocale } from "../../../common/i18n/locale";
import styles from "./ConfirmationPage.module.css";

/**
 * Confirmation — maquette « 06 - confirmation ».
 *
 * Trois blocs, dans l'ordre de la maquette : l'accusé de réception (anneau,
 * titre, référence, actions), puis le carnet de voyage — même billet à trois
 * volets que le dossier de réservation : photo, corps, talon perforé — puis
 * les prochaines étapes en cartes.
 *
 * On n'y arrive aujourd'hui que par la simulation de paiement : aucune
 * mutation de création n'est appelée, donc aucun dossier n'existe en base.
 */

const parseDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatShort = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  return new Intl.DateTimeFormat(currentLocale(), {
    day: "numeric",
    month: "short",
  }).format(d);
};

const formatRange = (start, end) => {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) return "";
  const options = { day: "numeric", month: "long", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(currentLocale(), options).formatRange(from, to);
  } catch {
    return `${new Intl.DateTimeFormat(currentLocale(), options).format(from)} – ${new Intl.DateTimeFormat(currentLocale(), options).format(to)}`;
  }
};

const nightsBetween = (start, end) => {
  const a = parseDate(start);
  const b = parseDate(end);
  if (!a || !b) return null;
  const n = Math.round((b - a) / 86400000);
  return n > 0 ? n : null;
};

/** Référence lisible, dérivée du voyage : même dossier, même référence. */
const buildReference = (tripPublicationId, tripDate) => {
  const seed = `${tripPublicationId ?? ""}${tripDate ?? ""}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return `MDV-${new Date().getFullYear()}-${String(hash).padStart(5, "0")}`;
};

export function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tripPublicationId, tripDate } = useParams();
  const { personInfo } = usePerson();
  const [copied, setCopied] = useState(false);

  const booking = location.state?.bookingPreview ?? null;
  const isSimulated = location.state?.simulated !== false;

  const travelers = useMemo(() => {
    if (Array.isArray(location.state?.travelers)) return location.state.travelers;

    try {
      const raw = sessionStorage.getItem(`preview-booking:${tripPublicationId}:${tripDate}`);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.travelers) ? parsed.travelers : [];
    } catch {
      return [];
    }
  }, [location.state, tripPublicationId, tripDate]);

  const reference = useMemo(
    () => buildReference(tripPublicationId, tripDate),
    [tripPublicationId, tripDate]
  );

  const startDate = booking?.selectedTripDate?.startDate;
  const endDate = booking?.selectedTripDate?.endDate;
  const nights = nightsBetween(startDate, endDate);
  const days = nights ? nights + 1 : null;
  const hasDates = Boolean(startDate && endDate);

  const total = booking?.total;
  const amountLabel =
    total?.value != null
      ? `${Number(total.value).toLocaleString(currentLocale())} ${total.currency ?? ""}`.trim()
      : "";

  /* Le premier voyageur d'abord : c'est lui qui part, et c'est le nom porté
     par le dossier. Le compte ne sert que de repli. */
  const firstName =
    travelers[0]?.firstName ||
    personInfo?.firstName ||
    t("profile.anonymous", "Voyageur");

  const extras = booking?.extras ?? [];
  /* Le carnet liste d'abord ce qui est compris dans le forfait, puis ce que
     le voyageur a ajouté — c'est l'ordre de la maquette. */
  const voucherLines = [
    ...(booking?.included ?? []).map((label) => ({ label, added: false })),
    ...extras.map((extra) => ({ label: extra.label, added: true })),
  ];

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* presse-papiers indisponible : la référence reste lisible à l'écran */
    }
  };

  const steps = [
    {
      key: "confirmed",
      icon: "iconCheck",
      done: true,
      status: t("confirmation.next.doneTag", "Fait"),
      title: t("confirmation.next.confirmed", "Confirmation"),
      text: t(
        "confirmation.next.confirmedText",
        "Votre réservation est enregistrée et votre règlement est accepté."
      ),
    },
    {
      key: "documents",
      icon: "iconEnvelope",
      status: t("confirmation.next.soonTag", "À venir"),
      title: t("confirmation.next.documents", "Documents de voyage"),
      text: t(
        "confirmation.next.documentsText",
        "Billets, bons d'échange et carnet de voyage vous seront transmis et resteront disponibles dans « Mes voyages »."
      ),
    },
    {
      key: "prepare",
      icon: "iconSuitcase",
      status: t("confirmation.next.soonTag", "À venir"),
      title: t("confirmation.next.prepare", "Préparer le départ"),
      text: t(
        "confirmation.next.prepareText",
        "Vérifiez la validité de vos pièces d'identité et les formalités de la destination."
      ),
    },
    {
      key: "departure",
      icon: "iconPlaneDeparture",
      status: hasDates ? formatShort(startDate) : t("confirmation.next.soonTag", "À venir"),
      title: t("confirmation.next.departure", "Départ"),
      text: t(
        "confirmation.next.departureText",
        "Votre agence reste joignable depuis la messagerie jusqu'au jour du départ."
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("booking.steps.confirmation", "Confirmation") },
          ]}
        />

        {isSimulated && (
          <div className={styles.notice}>
            <AlertBox
              type="warning"
              title={t("confirmation.demoTitle", "Mode démonstration")}
              message={t(
                "confirmation.demoNotice",
                "Le paiement a été simulé et aucun dossier n'a été créé. La référence ci-dessous n'existe pas en base."
              )}
            />
          </div>
        )}

        {/* ---------- Accusé de réception ---------- */}
        <section className={styles.hero}>
          <span className={styles.ring} aria-hidden="true">
            <Icon name="iconCheck" size="lg" />
          </span>

          <p className={styles.kicker}>
            <span aria-hidden="true" />
            {t("confirmation.kicker", "Réservation confirmée")}
            <span aria-hidden="true" />
          </p>

          <h1 className={styles.title}>
            {t("confirmation.thanks", { name: firstName, defaultValue: `Merci ${firstName},` })}{" "}
            <em>{t("confirmation.haveAGoodTrip", "bon voyage")}</em> !
          </h1>

          <p className={styles.lede}>
            {booking?.title ? (
              <>
                {t("confirmation.ledeBefore", "Votre voyage")}{" "}
                <b>{booking.title}</b>{" "}
                {t("confirmation.ledeAfter", "est officiellement réservé.")}
              </>
            ) : (
              t("confirmation.subtitlePlain", "Votre voyage est réservé.")
            )}
          </p>

          <p className={styles.reference}>
            <span>{t("confirmation.reference", "Référence du dossier")}</span>
            <strong>{reference}</strong>
            <button type="button" className={styles.copy} onClick={handleCopy}>
              <Icon name={copied ? "iconCheck" : "iconFile"} size="xs" />
              {copied
                ? t("confirmation.copied", "Copiée")
                : t("confirmation.copy", "Copier")}
            </button>
          </p>

          <div className={styles.heroActions}>
            <Button
              variant="primary"
              size="medium"
              iconName="iconArrowRight"
              iconPosition="right"
              label={t("confirmation.myTrips", "Voir mes voyages")}
              onClick={() => navigate("/my-trips")}
            />
            <Button
              variant="secondary"
              size="medium"
              iconName="iconEye"
              iconPosition="left"
              label={t("confirmation.backToTrip", "Revoir le voyage")}
              onClick={() => navigate(`/trips/${tripPublicationId}/${tripDate}`)}
            />
          </div>

          <BookingSteps current={4} />
        </section>

        {/* ---------- Le carnet de voyage ---------- */}
        {booking ? (
          <section className={styles.ticket} aria-label={t("confirmation.voucher", "Carnet de voyage")}>
            <div className={styles.ticketBand}>
              <span className={styles.bandBrand}>Mondoviva</span>
              <span className={styles.bandLabel}>
                {t("confirmation.voucherLabel", "Carnet de voyage · Travel voucher")}
              </span>
              <span className={styles.bandPill}>{reference}</span>
            </div>

            <div className={styles.ticketBody}>
              <div className={styles.ticketPhoto}>
                {booking.picture ? (
                  <img src={booking.picture} alt="" />
                ) : (
                  <span className={styles.noPhoto} />
                )}
                <span className={styles.stamp}>
                  {t("confirmation.confirmed", "Confirmé")} ✓
                </span>
                {booking.destination && (
                  <span className={styles.photoChip}>{booking.destination}</span>
                )}
                <strong className={styles.photoTitle}>{booking.title}</strong>
              </div>

              <div className={styles.ticketMain}>
                <dl className={styles.facts}>
                  <div>
                    <dt>{t("confirmation.facts.dates", "Dates réservées")}</dt>
                    <dd>{hasDates ? formatRange(startDate, endDate) : "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("confirmation.facts.duration", "Durée")}</dt>
                    <dd>
                      {days
                        ? t("confirmation.facts.nights", {
                            count: nights,
                            days,
                            defaultValue: `${days} jours / ${nights} nuits`,
                          })
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("confirmation.facts.travelers", "Voyageurs")}</dt>
                    <dd>{travelers.length || "—"}</dd>
                  </div>
                  {booking.destination && (
                    <div>
                      <dt>{t("confirmation.facts.destination", "Destination")}</dt>
                      <dd>{booking.destination}</dd>
                    </div>
                  )}
                </dl>

                {voucherLines.length > 0 && (
                  <div className={styles.included}>
                    <h2 className={styles.includedTitle}>
                      <Icon name="iconCheck" size="xs" />
                      {t("confirmation.extras", "Activités et options comprises")}
                    </h2>
                    <ul className={styles.extras}>
                      {voucherLines.map((line) => (
                        <li
                          key={line.label}
                          className={line.added ? styles.added : ""}
                        >
                          <Icon name={line.added ? "iconStar" : "iconCheck"} size="xs" />
                          {line.added ? (
                            <span>
                              <b>{t("confirmation.optionTaken", "Option prise")}</b>
                              {" : "}
                              {line.label}
                            </span>
                          ) : (
                            line.label
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className={styles.barcode} aria-hidden="true">
                  {reference.split("").join(" · ")}
                </p>
              </div>

              <aside className={styles.stub}>
                <p className={styles.stubTravelers}>
                  <strong>{travelers.length || "—"}</strong>
                  <span>{t("confirmation.facts.travelers", "Voyageurs")}</span>
                </p>

                <div className={styles.stubGrid}>
                  <div>
                    <span>{t("confirmation.facts.departure", "Départ")}</span>
                    <strong>{hasDates ? formatShort(startDate) : "—"}</strong>
                  </div>
                  <div>
                    <span>{t("confirmation.facts.return", "Retour")}</span>
                    <strong>{hasDates ? formatShort(endDate) : "—"}</strong>
                  </div>
                  <div>
                    <span>{t("confirmation.stub.nights", "Nuits")}</span>
                    <strong>{nights || "—"}</strong>
                  </div>
                  <div>
                    <span>{t("confirmation.stub.days", "Jours")}</span>
                    <strong>{days || "—"}</strong>
                  </div>
                </div>

                <p className={styles.stubNote}>
                  {t("confirmation.stubNote", "À présenter à l'arrivée et lors des activités")}
                </p>

                {amountLabel && (
                  <p className={styles.stubTotal}>
                    <span>{t("confirmation.paid", "Montant réglé")}</span>
                    <strong>{amountLabel}</strong>
                  </p>
                )}
              </aside>
            </div>
          </section>
        ) : (
          /* Page ouverte directement, sans passer par le tunnel : il n'y a rien
             à reprendre puisque rien n'est persisté. */
          <p className={styles.empty}>
            {t(
              "confirmation.noDetails",
              "Le détail du voyage n'est pas disponible : cette page a été ouverte sans passer par les étapes de réservation."
            )}
          </p>
        )}

        {/* ---------- Les prochaines étapes ---------- */}
        <section className={styles.next}>
          <p className={styles.nextKicker}>{t("confirmation.next.kicker", "Et maintenant ?")}</p>
          <h2 className={styles.nextTitle}>
            {t("confirmation.next.title", "Les prochaines étapes")}
          </h2>
          <p className={styles.nextLede}>
            {t(
              "confirmation.next.lede",
              "Ce qui se passe entre aujourd'hui et votre départ."
            )}
          </p>

          <ol className={styles.nextGrid}>
            {steps.map((step) => (
              <li
                key={step.key}
                className={`${styles.nstep} ${step.done ? styles.nstepDone : ""}`}
              >
                <span className={styles.nstepIcon} aria-hidden="true">
                  <Icon name={step.icon} size="sm" />
                </span>
                <span className={styles.nstepStatus}>{step.status}</span>
                <strong>{step.title}</strong>
                <span className={styles.nstepText}>{step.text}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

export default ConfirmationPage;
